/**
 * Экран «Данные» — §14.2 и §16, состояние сбора.
 *
 * Отвечает на вопрос, который человек задаёт вторым, сразу после «сколько
 * выручка»: **можно ли этой цифре верить сегодня**. Отсюда состав: когда
 * последний раз приходили данные, что в них неполно и чего не хватает.
 *
 * **Полнота `0 %` и «полнота не оценена» — разные сообщения** (§16.2), и
 * экран обязан их различать. Первое означает «данные не дошли», второе —
 * «мы не знаем, дошли ли». Второе тревожнее, и показать его нулём значило
 * бы подменить незнание фактом.
 *
 * **Список проблем не скрывается и не сворачивается.** §16.4 запрещает
 * молчаливое подавление: если алерты подавлены из-за неполноты, человек
 * должен видеть и это, и полноту, при которой их подавили.
 */

import { useQuery } from '@tanstack/react-query';
import { ErrorState, Loading } from '@shell/states';
import { formatShortDateTime } from '@shell/format';
import { financeApi } from '../api';
import type { DataScreen, SourceState } from '../api';
import { FreshnessBadge, Section } from '../components';

/** Как называется разрез человеку. Коды разрезов — слова из модели
 *  данных, и человеку они не показываются никогда (§7 правил интерфейса). */
const SCOPE_TITLES: Record<string, string> = {
  sales: 'Продажи',
  inventory: 'Себестоимость',
};

export function Data() {
  const query = useQuery({ queryKey: ['finance', 'data'], queryFn: financeApi.data });

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const data = query.data as DataScreen;

  return (
    <div className="screen">
      <FreshnessBadge freshness={data.freshness} />

      {data.sources.map((source) => (
        <Section key={source.scope} title={SCOPE_TITLES[source.scope] ?? source.scope}>
          <SourceCard source={source} />
        </Section>
      ))}

      <Section title="Откуда берутся цифры">
        <p>
          Продажи читаются из кассовой системы ресторана, себестоимость — из складской.
          Данные забирает служба, установленная в самом ресторане: она сама выходит на
          связь и ничего не открывает наружу.
        </p>
        <p className="faint">
          Остановить сбор может владелец системы — командой <code>/stop_collector</code> в
          боте. Это делают, если в ресторане тормозит касса: её работа важнее свежести
          наших цифр.
        </p>
      </Section>
    </div>
  );
}

function SourceCard({ source }: { source: SourceState }) {
  if (!source.started) {
    return (
      <p className="faint">
        Сбор по этому источнику ещё ни разу не отработал. Так и должно быть до первой
        выгрузки.
      </p>
    );
  }

  return (
    <>
      <dl className="facts">
        <div>
          <dt>Последнее обновление</dt>
          <dd>{source.at ? formatShortDateTime(source.at) : '—'}</dd>
        </div>
        <div>
          <dt>Данные доехали до</dt>
          <dd>{source.synced_until ? formatShortDateTime(source.synced_until) : '—'}</dd>
        </div>
        <div>
          <dt>Полнота периода</dt>
          <dd>
            {/* `null` — «не оценена», и это не ноль. Разные сообщения
                (§16.2): «данные не дошли» и «мы не знаем, дошли ли». */}
            {source.completeness_pct === null || source.completeness_pct === undefined
              ? 'не оценена'
              : `${source.completeness_pct.toFixed(0)} %`}
          </dd>
        </div>
        <div>
          <dt>Смена закрыта</dt>
          <dd>
            {source.shift_closed === null || source.shift_closed === undefined
              ? 'неизвестно'
              : source.shift_closed
                ? 'да'
                : 'нет — день ещё идёт'}
          </dd>
        </div>
        {typeof source.missing_documents === 'number' && source.missing_documents > 0 && (
          <div>
            <dt>Не хватает документов</dt>
            <dd>{source.missing_documents}</dd>
          </div>
        )}
      </dl>

      {source.issues && source.issues.length > 0 && (
        <ul className="issues">
          {source.issues.map((issue, index) => (
            <li key={index}>{issue}</li>
          ))}
        </ul>
      )}
    </>
  );
}
